import { useState, useRef, useCallback, useEffect } from "react";

const getAudioDuration = (blob: Blob, fallbackSeconds: number): Promise<number> => {
	return new Promise((resolve) => {
		const audio = new Audio();
		audio.preload = "metadata";

		// Fallback if metadata loading takes too long (e.g. Firefox with blob URLs)
		const timeout = setTimeout(() => {
			URL.revokeObjectURL(audio.src);
			resolve(fallbackSeconds);
		}, 1000);

		const cleanup = (duration: number) => {
			clearTimeout(timeout);
			URL.revokeObjectURL(audio.src);
			resolve(duration);
		};

		audio.addEventListener("loadedmetadata", () => {
			if (isFinite(audio.duration) && audio.duration > 0) {
				cleanup(Math.round(audio.duration));
				return;
			}

			// Chrome returns Infinity for blob URLs — seek to force duration calculation
			audio.currentTime = Number.MAX_SAFE_INTEGER;
			audio.addEventListener("timeupdate", function handler() {
				audio.removeEventListener("timeupdate", handler);
				if (isFinite(audio.duration) && audio.duration > 0) {
					cleanup(Math.round(audio.duration));
				} else {
					cleanup(fallbackSeconds);
				}
			});
		});

		audio.src = URL.createObjectURL(blob);
	});
};

interface UseAudioRecorderReturn {
	// State
	isRecording: boolean;
	isPaused: boolean;
	isProcessing: boolean;
	recordedBlob: Blob | null;
	recordedUrl: string | null;
	error: string | null;
	hasPermission: boolean | null;
	isInitializing: boolean;
	recordingTime: number;
	recordedDuration: number | null;

	// Actions
	initializeMicrophone: () => Promise<boolean>;
	startRecording: () => void;
	stopRecording: () => void;
	pauseRecording: () => void;
	resumeRecording: () => void;
	resetRecording: () => void;
	cleanup: () => void;
}

export function useAudioRecorder(): UseAudioRecorderReturn {
	const [isRecording, setIsRecording] = useState(false);
	const [isPaused, setIsPaused] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
	const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [hasPermission, setHasPermission] = useState<boolean | null>(null);
	const [isInitializing, setIsInitializing] = useState(false);
	const [recordingTime, setRecordingTime] = useState(0);
	const [recordedDuration, setRecordedDuration] = useState<number | null>(null);

	const streamRef = useRef<MediaStream | null>(null);
	const mediaRecorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const cumulativeTimeRef = useRef(0);
	const segmentStartRef = useRef<number | null>(null);
	const recordedDurationRef = useRef<number | null>(null);

	const cleanup = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			mediaRecorderRef.current.stop();
		}
		mediaRecorderRef.current = null;

		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}

		if (recordedUrl) {
			URL.revokeObjectURL(recordedUrl);
		}

		chunksRef.current = [];
	}, [recordedUrl]);

	const initializeMicrophone = useCallback(async (): Promise<boolean> => {
		setIsInitializing(true);
		setError(null);

		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});

			streamRef.current = stream;
			setHasPermission(true);
			setIsInitializing(false);
			return true;
		} catch (err) {
			setHasPermission(false);
			setIsInitializing(false);

			if (err instanceof Error) {
				if (err.name === "NotAllowedError") {
					setError("Microphone access denied. Please allow microphone access.");
				} else if (err.name === "NotFoundError") {
					setError("No microphone found on this device.");
				} else {
					setError(`Failed to access microphone: ${err.message}`);
				}
			} else {
				setError("Failed to access microphone. Please try again.");
			}
			return false;
		}
	}, []);

	const startRecording = useCallback(() => {
		if (!streamRef.current) {
			setError("Microphone not initialized. Please try again.");
			return;
		}

		chunksRef.current = [];
		setError(null);

		try {
			const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
				? "audio/webm;codecs=opus"
				: MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
					? "audio/ogg;codecs=opus"
					: "audio/mp4";

			const mediaRecorder = new MediaRecorder(streamRef.current, {
				mimeType,
				audioBitsPerSecond: 128000,
			});

			mediaRecorder.ondataavailable = (event) => {
				if (event.data.size > 0) {
					chunksRef.current.push(event.data);
				}
			};

			mediaRecorder.onstop = async () => {
				const blob = new Blob(chunksRef.current, { type: mimeType });
				const timerSeconds = Math.round(cumulativeTimeRef.current / 1000);
				const durationSeconds = await getAudioDuration(blob, timerSeconds);
				setRecordedBlob(blob);
				const url = URL.createObjectURL(blob);
				setRecordedUrl(url);
				setRecordedDuration(durationSeconds);
				setIsProcessing(false);
			};

			mediaRecorder.onerror = () => {
				setError("Recording failed. Please try again.");
				setIsRecording(false);
				setIsProcessing(false);
			};

			mediaRecorderRef.current = mediaRecorder;
			mediaRecorder.start(1000);
			setIsRecording(true);
			setRecordingTime(0);
			setRecordedDuration(null);
			cumulativeTimeRef.current = 0;
			segmentStartRef.current = Date.now();

			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		} catch {
			setError("Failed to start recording. Please try again.");
		}
	}, []);

	const stopRecording = useCallback(() => {
		// Calculate final duration accounting for pauses
		if (segmentStartRef.current !== null) {
			cumulativeTimeRef.current += Date.now() - segmentStartRef.current;
			segmentStartRef.current = null;
		}
		const durationSeconds = Math.round(cumulativeTimeRef.current / 1000);
		// Store in ref so it's synchronously available in onstop handler's render
		recordedDurationRef.current = durationSeconds;

		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			setIsProcessing(true);
			mediaRecorderRef.current.stop();
		}
		setIsRecording(false);
		setIsPaused(false);

		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const pauseRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
			mediaRecorderRef.current.pause();
			setIsPaused(true);

			// Accumulate elapsed time for this segment
			if (segmentStartRef.current !== null) {
				cumulativeTimeRef.current += Date.now() - segmentStartRef.current;
				segmentStartRef.current = null;
			}

			if (timerRef.current) {
				clearInterval(timerRef.current);
				timerRef.current = null;
			}
		}
	}, []);

	const resumeRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state === "paused") {
			mediaRecorderRef.current.resume();
			setIsPaused(false);

			// Start a new timing segment
			segmentStartRef.current = Date.now();

			timerRef.current = setInterval(() => {
				setRecordingTime((prev) => prev + 1);
			}, 1000);
		}
	}, []);

	const resetRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
			mediaRecorderRef.current.stop();
		}

		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}

		if (recordedUrl) {
			URL.revokeObjectURL(recordedUrl);
		}

		chunksRef.current = [];
		setRecordedBlob(null);
		setRecordedUrl(null);
		setIsRecording(false);
		setIsPaused(false);
		setIsProcessing(false);
		setRecordingTime(0);
		setRecordedDuration(null);
		recordedDurationRef.current = null;
		cumulativeTimeRef.current = 0;
		segmentStartRef.current = null;
		setError(null);
	}, [recordedUrl]);

	useEffect(() => {
		return () => {
			cleanup();
		};
	}, [cleanup]);

	return {
		isRecording,
		isPaused,
		isProcessing,
		recordedBlob,
		recordedUrl,
		error,
		hasPermission,
		isInitializing,
		recordingTime,
		recordedDuration,
		initializeMicrophone,
		startRecording,
		stopRecording,
		pauseRecording,
		resumeRecording,
		resetRecording,
		cleanup,
	};
}
